import { PDFDocument } from "pdf-lib";
import { downloadBlob } from "operations/operations";

export const renderEmbeddedUpcLabel = ({
  qrCodeBase64,
  upc,
  product,
  packedOn,
  expiresOn,
}) =>
  new Promise((resolve, reject) => {
    if (!qrCodeBase64) {
      reject(new Error("qrCodeBase64 is required"));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `data:image/png;base64,${qrCodeBase64}`;

    img.onload = () => {
      try {
        const qrSize = 600;
        const cellPadding = 28;
        const fontName = "Arial";
        const fontSize = 48;
        const lineHeight = fontSize * 1.6;
        const borderWidth = 10;

        // Only the fields from the printed sticker.
        const rawLines = [
          ["UPC", upc],
          ["Product", product],
          ["Packed on", packedOn],
          ["Expires on", expiresOn],
        ]
          .filter(([, v]) => v !== undefined && v !== null && String(v).length > 0)
          .map(([k, v]) => ({ key: `${k}:`, value: String(v) }));

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // 50/50 layout: each cell is the QR's natural size + padding.
        const cellWidth = qrSize + cellPadding * 2;
        const leftCellWidth = cellWidth;
        const rightCellWidth = rawLines.length ? cellWidth : 0;
        const textMaxWidth = rightCellWidth - cellPadding * 2;

        const wrapPair = ({ key, value }) => {
          ctx.font = `bold ${fontSize}px ${fontName}`;
          const keyWidth = ctx.measureText(`${key} `).width;
          ctx.font = `${fontSize}px ${fontName}`;

          const words = String(value).split(/\s+/).filter(Boolean);
          const segments = [];
          let currentText = "";
          let availableWidth = textMaxWidth - keyWidth;
          let isFirst = true;

          words.forEach((word) => {
            const candidate = currentText ? `${currentText} ${word}` : word;
            if (ctx.measureText(candidate).width <= availableWidth) {
              currentText = candidate;
            } else {
              segments.push({ text: currentText, isFirst });
              isFirst = false;
              currentText = word;
              availableWidth = textMaxWidth;
            }
          });
          segments.push({ text: currentText, isFirst });
          return segments.map((seg) => ({ key, value: seg.text, isFirst: seg.isFirst }));
        };

        const lines = rawLines.flatMap(wrapPair);
        const textBlockHeight = lines.length * lineHeight;

        const cellHeight = Math.max(qrSize, textBlockHeight) + cellPadding * 2;
        const canvasWidth = Math.ceil(leftCellWidth + rightCellWidth);
        const canvasHeight = Math.ceil(cellHeight);

        // Render at 2x for sharper output.
        const scale = 2;
        canvas.width = canvasWidth * scale;
        canvas.height = canvasHeight * scale;
        ctx.scale(scale, scale);

        // Background.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // QR (left cell, centered).
        ctx.imageSmoothingEnabled = false;
        const qrX = (leftCellWidth - qrSize) / 2;
        const qrY = (canvasHeight - qrSize) / 2;
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
        ctx.imageSmoothingEnabled = true;

        // Text block (right cell, vertically centered).
        if (lines.length) {
          const textX = leftCellWidth + cellPadding;
          const textY = (canvasHeight - textBlockHeight) / 2;

          ctx.fillStyle = "#000000";
          ctx.textAlign = "left";
          ctx.textBaseline = "top";

          let y = textY;
          lines.forEach((line) => {
            if (line.isFirst) {
              ctx.font = `bold ${fontSize}px ${fontName}`;
              const keyWidth = ctx.measureText(`${line.key} `).width;
              ctx.fillText(line.key, textX, y);
              ctx.font = `${fontSize}px ${fontName}`;
              ctx.fillText(line.value, textX + keyWidth, y);
            } else {
              ctx.font = `${fontSize}px ${fontName}`;
              ctx.fillText(line.value, textX, y);
            }
            y += lineHeight;
          });
        }

        // Single outer border around the whole label, inset so all four
        // edges render fully.
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = borderWidth;
        ctx.strokeRect(
          borderWidth,
          borderWidth,
          canvasWidth - borderWidth * 2,
          canvasHeight - borderWidth * 2
        );

        // Vertical divider between the QR cell and the info cell.
        if (lines.length) {
          ctx.beginPath();
          ctx.moveTo(leftCellWidth, borderWidth);
          ctx.lineTo(leftCellWidth, canvasHeight - borderWidth);
          ctx.stroke();
        }

        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: canvasWidth,
          height: canvasHeight,
        });
      } catch (e) {
        reject(e);
      }
    };

    img.onerror = () => reject(new Error("Failed to load QR code image"));
  });

export const downloadEmbeddedUpcLabelPdf = async ({
  qrCodeBase64,
  upc,
  product,
  packedOn,
  expiresOn,
  fileName = "embedded_upc_label",
}) => {
  const { dataUrl, width, height } = await renderEmbeddedUpcLabel({
    qrCodeBase64,
    upc,
    product,
    packedOn,
    expiresOn,
  });

  const pngBytes = await fetch(dataUrl).then((r) => r.arrayBuffer());

  const pdfDoc = await PDFDocument.create();
  const pngImage = await pdfDoc.embedPng(pngBytes);

  // Page sized to label (1px = 1pt) so the label fills the page.
  const page = pdfDoc.addPage([width, height]);
  page.drawImage(pngImage, { x: 0, y: 0, width, height });

  const pdfBytes = await pdfDoc.save();
  downloadBlob(pdfBytes, `${fileName}.pdf`, "application/pdf");
};
